/**
 * views/Rbac/CountryManagement.js
 * ─────────────────────────────────────────────────────────────────────────────
 * SUPER-ADMIN-ONLY — manage which operational country each staff member belongs
 * to. This is the display companion to the backend's `assignCountry`
 * (super-admin-only, audited via `COUNTRY_ASSIGNED`) and `enforceStaffCountry`
 * middleware. See `Lusuku_MultiNational_RBAC_CountryScoping_Design.md` §4, §7.
 *
 * Country is a DATA-ACCESS BOUNDARY, not a UI preference:
 *   • Only super-admins may change a staffer's country (server enforces; this
 *     page is defense-in-depth — it also gates on useCountry().canSwitch).
 *   • `country`      — the single ISO-2 home market a staffer is bound to.
 *   • `countryScope` — an optional array of ISO-2 codes for the rare REGIONAL
 *     role that legitimately spans a few markets. Empty ⇒ single-country.
 *   • `isSuperAdmin` — the boolean capability meaning "may operate any country
 *     and switch between them" (canonical forward-looking flag; distinct from
 *     user_role === "SUPERUSER").
 *
 * Data plumbing (helpers created in ../api/index.js — imported, never recreated):
 *   fetchStaffUsers(params)                                     → GET   /api/rbac/staff
 *   assignStaffCountry(userId, { country, countryScope, isSuperAdmin })
 *                                                               → PATCH /api/rbac/staff/:userId/country
 *
 * The staff-list response shape is normalized defensively (see `extractStaff`)
 * because the exact envelope (array | {data} | {data:{data|users|staff}}) is
 * owned by the parallel API agent and may differ from this view's assumption.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchStaffUsers, assignStaffCountry } from "../../api/index.js";
import {
  MARKETS,
  SUPPORTED_COUNTRIES,
} from "../../config/markets.js";
import { useCountry } from "../../context/CountryContext.js";
import CountryFlag from "../../components/CountryFlag.js";
import AppSelect from "../../components/ui/AppSelect";
import { AppDialog } from "../../components/ui";

// ---------------------------------------------------------------------------
// STYLES — mirror the RBACManager inline-style design system so this admin
// tool page is visually consistent with the rest of the RBAC surface.
// ---------------------------------------------------------------------------

const S = {
  page:         { background: "#f8fafc", minHeight: "100vh", padding: "24px 20px", fontFamily: "'Inter', system-ui, sans-serif" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  title:        { fontSize: 26, fontWeight: 700, color: "#0f172a", margin: 0 },
  subtitle:     { fontSize: 13, color: "#64748b", marginTop: 4, maxWidth: 640 },
  card:         { background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 12 },
  badge:        (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 4, background: bg, color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }),
  table:        { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:           { textAlign: "left", padding: "10px 12px", background: "#f1f5f9", fontWeight: 600, color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" },
  td:           { padding: "12px 12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "middle" },
  btnPrimary:   { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnGhost:     { background: "transparent", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  input:        { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" },
  select:       { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none", boxSizing: "border-box" },
  label:        { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block" },
  modal:        { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 },
  modalBox:     { background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  modalTitle:   { fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 6 },
  modalSub:     { fontSize: 12, color: "#64748b", marginBottom: 18 },
  avatar:       (bg) => ({ width: 36, height: 36, borderRadius: "50%", background: bg, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }),
  searchBar:    { display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" },
  pill:         (color, bg) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 700 }),
  alert:        (type) => ({ padding: "12px 16px", borderRadius: 8, marginBottom: 14, fontSize: 13, background: type === "error" ? "#fef2f2" : type === "success" ? "#f0fdf4" : "#eff6ff", color: type === "error" ? "#dc2626" : type === "success" ? "#047857" : "#1d4ed8", border: `1px solid ${type === "error" ? "#fecaca" : type === "success" ? "#bbf7d0" : "#bfdbfe"}` }),
  spinner:      { display: "inline-block", width: 18, height: 18, border: "2px solid #e2e8f0", borderTopColor: "#1d4ed8", borderRadius: "50%", animation: "spin 0.6s linear infinite" },
  checkRow:     { display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 13, color: "#1e293b" },
  toggle:       (on) => ({ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, color: on ? "#047857" : "#475569" }),
  toggleTrack:  (on) => ({ width: 40, height: 22, borderRadius: 12, background: on ? "#047857" : "#cbd5e1", position: "relative", transition: "background 0.15s" }),
  toggleKnob:   (on) => ({ position: "absolute", top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.25)" }),
};

// ---------------------------------------------------------------------------
// HELPERS — pure
// ---------------------------------------------------------------------------

const AVATAR_COLORS = ["#1d4ed8", "#7c3aed", "#047857", "#b45309", "#dc2626", "#0369a1", "#be185d"];
const getAvatarColor = (str = "") => AVATAR_COLORS[(str.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const getInitials    = (name = "") => name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const shortId        = (id = "") => (id ? id.toString().slice(-6) : "");
const apiErrMsg      = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/**
 * Normalize the staff-list response into an array of staffers regardless of the
 * envelope the API helper resolves to. The exact shape is owned by the parallel
 * API agent, so accept the common possibilities and fail safe to [].
 *   - the resolved value is already an array
 *   - axios-style { data: [...] }
 *   - RBAC-style  { data: { data: [...] } } / { data: { users|staff: [...] } }
 */
const extractStaff = (res) => {
  if (Array.isArray(res)) return res;
  const body = res?.data ?? res;
  if (Array.isArray(body)) return body;
  const inner = body?.data ?? body;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner?.users)) return inner.users;
  if (Array.isArray(inner?.staff)) return inner.staff;
  if (Array.isArray(body?.users)) return body.users;
  if (Array.isArray(body?.staff)) return body.staff;
  return [];
};

const marketLabel = (iso2) => {
  const m = MARKETS[iso2];
  return m ? m.name : iso2 || "—";
};

// ---------------------------------------------------------------------------
// PRIMITIVES
// ---------------------------------------------------------------------------

const Spinner = () => <span style={S.spinner} />;

const LoadingCenter = ({ text = "Loading..." }) => (
  <div style={{ textAlign: "center", padding: 30, color: "#64748b" }}>
    <Spinner /> {text}
  </div>
);

const Alert = ({ type, msg, onClose }) =>
  msg ? (
    <div style={{ ...S.alert(type), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span>{msg}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit", padding: 0, marginLeft: 10 }}
          aria-label="Dismiss"
        >
          x
        </button>
      )}
    </div>
  ) : null;

const CountryPill = ({ iso2 }) => {
  return (
    <span style={{ ...S.pill("#0f172a", "#e2e8f0"), gap: 6 }}>
      <CountryFlag country={iso2} width={18} decorative />
      {iso2 || "—"}
    </span>
  );
};

const UserAvatar = ({ name, email }) => {
  const str = name || email || "";
  return <div style={S.avatar(getAvatarColor(str))}>{getInitials(str)}</div>;
};

// ---------------------------------------------------------------------------
// ASSIGN-COUNTRY MODAL
// ---------------------------------------------------------------------------

const AssignCountryModal = ({ staffer, onClose, onSaved, onToast }) => {
  const initialScope = Array.isArray(staffer.countryScope) ? staffer.countryScope : [];
  const [country, setCountry]         = useState(staffer.country || "UG");
  const [countryScope, setCountryScope] = useState(initialScope);
  const [isSuperAdmin, setIsSuperAdmin] = useState(staffer.isSuperAdmin === true);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState("");

  const toggleScope = (iso2) =>
    setCountryScope((prev) =>
      prev.includes(iso2) ? prev.filter((c) => c !== iso2) : [...prev, iso2]
    );

  const save = useCallback(async () => {
    setSaving(true);
    setErr("");
    try {
      // Only send a non-empty countryScope; an empty array means single-country
      // (the backend treats absent/empty scope as "use home country").
      const payload = {
        country,
        isSuperAdmin,
        countryScope: countryScope.length ? countryScope : [],
      };
      await assignStaffCountry(staffer._id, payload);
      onToast("success", `Updated ${staffer.name || staffer.email || "staffer"}'s country access.`);
      onSaved({ ...staffer, ...payload });
      onClose();
    } catch (e) {
      setErr(apiErrMsg(e, "Failed to update country access."));
    } finally {
      setSaving(false);
    }
  }, [country, countryScope, isSuperAdmin, staffer, onSaved, onClose, onToast]);

  return (
    <AppDialog open onClose={onClose} hideClose busy={saving}>
        <div style={S.modalTitle}>Country access</div>
        <div style={S.modalSub}>
          {staffer.name || staffer.email} · {staffer.user_role || staffer.userType || "staff"}
        </div>

        <Alert type="error" msg={err} onClose={() => setErr("")} />

        {/* Home country ----------------------------------------------------- */}
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Home country (operational market)</label>
          <AppSelect
            style={S.select}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {SUPPORTED_COUNTRIES.map((iso2) => (
              <option key={iso2} value={iso2}>
                {marketLabel(iso2)} ({iso2})
              </option>
            ))}
          </AppSelect>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            The single market this staffer is bound to for reads and writes.
          </div>
        </div>

        {/* Country scope (regional roles) ----------------------------------- */}
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Regional scope (optional)</label>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8 }}>
            For the rare regional role that spans multiple markets. Leave all
            unchecked for a normal single-country staffer.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 4 }}>
            {SUPPORTED_COUNTRIES.map((iso2) => {
              const checked = countryScope.includes(iso2);
              return (
                <label
                  key={iso2}
                  style={{ ...S.checkRow, background: checked ? "#eff6ff" : "transparent" }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleScope(iso2)}
                  />
                  <CountryFlag country={iso2} width={20} decorative />
                  {marketLabel(iso2)}
                </label>
              );
            })}
          </div>
        </div>

        {/* Super-admin toggle ----------------------------------------------- */}
        <div style={{ marginBottom: 8 }}>
          <label style={S.label}>Super-admin (may operate & switch any country)</label>
          <div
            style={S.toggle(isSuperAdmin)}
            onClick={() => setIsSuperAdmin((v) => !v)}
            role="switch"
            aria-checked={isSuperAdmin}
          >
            <span style={S.toggleTrack(isSuperAdmin)}>
              <span style={S.toggleKnob(isSuperAdmin)} />
            </span>
            {isSuperAdmin ? "Super-admin" : "Country-scoped"}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
            Grants cross-country reach and the country switcher. Every change here
            is audited (COUNTRY_ASSIGNED).
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
          <button style={S.btnGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={S.btnPrimary} onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
    </AppDialog>
  );
};

// ---------------------------------------------------------------------------
// MAIN VIEW
// ---------------------------------------------------------------------------

const CountryManagement = () => {
  const { canSwitch } = useCountry();

  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadErr, setLoadErr]   = useState("");
  const [search, setSearch]     = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [editing, setEditing]   = useState(null);
  const [toast, setToast]       = useState(null); // { type, msg }

  const onToast = useCallback((type, msg) => setToast({ type, msg }), []);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const res = await fetchStaffUsers({ limit: 500 });
      setStaff(extractStaff(res));
    } catch (e) {
      setLoadErr(apiErrMsg(e, "Failed to load staff users."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canSwitch) loadStaff();
    else setLoading(false);
  }, [canSwitch, loadStaff]);

  // Reflect a saved row back into the local list without a full refetch.
  const onSaved = useCallback((updated) => {
    setStaff((prev) => prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s)));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (staff || []).filter((s) => {
      if (countryFilter && (s.country || "") !== countryFilter) return false;
      if (!q) return true;
      return (
        (s.name || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.user_role || "").toLowerCase().includes(q)
      );
    });
  }, [staff, search, countryFilter]);

  // Defense-in-depth: the route is already super-gated, but never render staff
  // country controls to a non-super identity even if they reach this component.
  if (!canSwitch) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.sectionTitle}>Access restricted</div>
          <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
            Country access management is available to super-admins only. Your
            account is scoped to a single operational country.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Country Access</h1>
          <p style={S.subtitle}>
            Assign each staff member to their operational market. Country is a
            data-access boundary — a staffer only sees their country's data.
            Only super-admins may change these; every change is audited.
          </p>
        </div>
        <button style={S.btnGhost} onClick={loadStaff} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <Alert type={toast?.type} msg={toast?.msg} onClose={() => setToast(null)} />

      <div style={S.card}>
        <div style={S.searchBar}>
          <input
            style={{ ...S.input, maxWidth: 320 }}
            placeholder="Search by name, email, or role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <AppSelect
            style={{ ...S.select, maxWidth: 220 }}
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
          >
            <option value="">All countries</option>
            {SUPPORTED_COUNTRIES.map((iso2) => (
              <option key={iso2} value={iso2}>
                {marketLabel(iso2)}
              </option>
            ))}
          </AppSelect>
          <span style={{ fontSize: 12, color: "#64748b", marginLeft: "auto" }}>
            {filtered.length} of {staff.length} staff
          </span>
        </div>

        <Alert type="error" msg={loadErr} onClose={() => setLoadErr("")} />

        {loading ? (
          <LoadingCenter text="Loading staff users..." />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13 }}>
            {staff.length === 0
              ? "No staff users found."
              : "No staff match the current filters."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Staff</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Home country</th>
                  <th style={S.th}>Regional scope</th>
                  <th style={S.th}>Super-admin</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s._id || s.email}>
                    <td style={S.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <UserAvatar name={s.name} email={s.email} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.name || "—"}</div>
                          <div style={{ fontSize: 12, color: "#64748b" }}>{s.email}</div>
                          {s._id && (
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>#{shortId(s._id)}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={S.td}>
                      <span style={S.badge("#e0e7ff", "#3730a3")}>
                        {s.user_role || s.userType || "—"}
                      </span>
                    </td>
                    <td style={S.td}>
                      <CountryPill iso2={s.country} />
                    </td>
                    <td style={S.td}>
                      {Array.isArray(s.countryScope) && s.countryScope.length ? (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {s.countryScope.map((iso2) => (
                            <CountryPill key={iso2} iso2={iso2} />
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      {s.isSuperAdmin === true ? (
                        <span style={S.pill("#7c3aed", "#ede9fe")}>Super-admin</span>
                      ) : (
                        <span style={{ color: "#cbd5e1", fontSize: 12 }}>No</span>
                      )}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button style={S.btnGhost} onClick={() => setEditing(s)}>
                        Change country
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <AssignCountryModal
          staffer={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onToast={onToast}
        />
      )}
    </div>
  );
};

export default CountryManagement;
